import {
  getSupportBanks,
  getUserInformation,
  sendSmsOrBindCard,
  getBankInfoByCardNo,
  submitCardBind,
  getCardNoByUserBankId,
} from '@/api/bank-card';
import {
  CardTypeEnum,
  BankInfoOfCardNo,
  SupportBankCard,
  BindCardFlowTypeMap,
  BindCardParamsTypes,
} from '@/api/bank-card/types';

import { ActionContext, Module } from 'vuex';
import store from '@/store';
import RootStateType from '@/store/types';
import generateMutations, {
  UPDATE_STATE_TYPE,
} from '@/store/generate-mutations';

import BindContracts from './components/contract-list/bind-contracts';
import type { StateType } from './types';
import { FormInfomation } from '../card-list/types';

const trim = (str: string): string => str.replace(/\s/g, '');

// 判断银行是否被支持
const checkIsBankSupported = (
  bankInfo: BankInfoOfCardNo | undefined,
  supportList: SupportBankCard[],
): boolean | undefined => {
  if (bankInfo && supportList.length) {
    return supportList.some((item) => item.name === bankInfo.bankName);
  }

  return undefined;
};

const moduleStore: Module<StateType, RootStateType> = {
  namespaced: true,
  state: {
    userName: '',
    cardNo: '',
    defaultCardNo: '', // 未脱敏前的卡号
    idCardNo: '',
    shouldMaskCardNo: false,
    bankInfo: undefined,
    isBankSupported: undefined,
    reservePhone: '',
    defaultReservePhone: '',
    shouldMaskPhoneNo: false,
    smsCode: '',
    supportList: [],
    contractList: [],
    sendSmsTimeStamp: 0,
    sendSmsOrBindCardResult: undefined,
    historyLength: undefined,
  },

  actions: {
    // 获取用户信息
    async getUserInfo(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      try {
        const userInfo = await getUserInformation();
        context.dispatch(UPDATE_STATE_TYPE, {
          userName: userInfo.userName || '',
          reservePhone: userInfo.phone || '',
          defaultReservePhone: userInfo.phone || '',
          shouldMaskPhoneNo: !!userInfo.phone,
          idCardNo: userInfo.idCardNo || '',
        });

        return Promise.resolve();
      } catch (error) {
        return Promise.resolve();
      }
    },

    // 获取归属银行名称以及对应的卡类型
    async getBankInfoByCardNo(
      context: ActionContext<StateType, RootStateType>,
      payload: {
        commodityId: string,
        cardNo: string,
        checkCapital: boolean,
        capitalCode: string,
      },
    ): Promise<void> {
      try {
        const { supportList } = context.state;
        const {
          cardNo, checkCapital, capitalCode, commodityId,
        } = payload;
        const bankInfo = await getBankInfoByCardNo(cardNo, checkCapital, capitalCode, commodityId);
        const isBankSupported = checkIsBankSupported(bankInfo, supportList);

        context.dispatch(UPDATE_STATE_TYPE, {
          bankInfo,
          isBankSupported,
        });

        return Promise.resolve();
      } catch (error) {
        // 接口抛错，提示异常
        context.dispatch(UPDATE_STATE_TYPE, {
          bankInfo: undefined,
        });
        return Promise.reject();
      }
    },

    // 获取支持的银行卡列表
    async getSupportList(
      context: ActionContext<StateType, RootStateType>,
      commodityId: string,
      capitalCode?: string,
    ): Promise<void> {
      try {
        const { bankInfo } = context.state;
        const supportList = await getSupportBanks(commodityId, capitalCode);
        const isBankSupported = checkIsBankSupported(bankInfo, supportList);

        context.dispatch(UPDATE_STATE_TYPE, {
          supportList,
          isBankSupported,
        });

        return Promise.resolve();
      } catch (error) {
        return Promise.resolve();
      }
    },

    // 根据 userBankId 获取 cardNo
    async getCardNoByUserBankId(
      context: ActionContext<StateType, RootStateType>,
      userBankId: string,
    ): Promise<void> {
      try {
        const data = await getCardNoByUserBankId(userBankId);
        context.dispatch(UPDATE_STATE_TYPE, {
          cardNo: data.cardNo,
          defaultCardNo: data.cardNo,
          shouldMaskCardNo: !!data.cardNo,
        });

        return Promise.resolve();
      } catch (error) {
        return Promise.resolve();
      }
    },

    // 获取协议列表
    async getContractList(
      context: ActionContext<StateType, RootStateType>,
      payload: {
        commodityId: string;
        capitalCode?: string;
      },
    ): Promise<void> {
      try {
        const {
          cardNo,
          bankInfo,
          defaultCardNo,
          shouldMaskCardNo,
        } = context.state;

        if (cardNo && bankInfo) {
          const { commodityId, capitalCode } = payload;
          context.dispatch(UPDATE_STATE_TYPE, {
            contractList: await new BindContracts().getContracts({
              cardNo: trim(
                shouldMaskCardNo
                  ? defaultCardNo
                  : cardNo,
              ),
              commodityId,
              cardName: bankInfo.bankName,
              capitalCode,
            }, {
              showLoading: undefined,
            }),
          });
        }

        return Promise.resolve();
      } catch (error) {
        return Promise.resolve();
      }
    },

    // 尝试发送短信验证码或者直接提交成功
    async sendSmsOrBindCard(
      context: ActionContext<StateType, RootStateType>,
      params: BindCardParamsTypes,
    ): Promise<boolean> {
      try {
        const {
          shouldMaskCardNo,
          defaultCardNo,
          cardNo,
          shouldMaskPhoneNo,
          defaultReservePhone,
          reservePhone,
        } = context.state;
        // 先触发倒计时
        context.dispatch(UPDATE_STATE_TYPE, {
          sendSmsTimeStamp: new Date().getTime(),
        });

        const result = await sendSmsOrBindCard({
          commodityId: params.commodityId,
          bindCardFlowType: BindCardFlowTypeMap[params.entryFrom],
          cardType: CardTypeEnum.DebitCard, // TODO,
          cardNo: trim(shouldMaskCardNo
            ? defaultCardNo
            : cardNo),
          reservePhone: trim(
            shouldMaskPhoneNo
              ? defaultReservePhone
              : reservePhone,
          ),
          capitalCode: params.capitalCode,
          loanOrderId: params.loanOrderId,
        });

        context.dispatch(UPDATE_STATE_TYPE, {
          sendSmsOrBindCardResult: result,
        });

        // 不需要发送短信验证码
        if (result.bandStatus) {
          context.dispatch(UPDATE_STATE_TYPE, {
            sendSmsTimeStamp: 0,
          });
        }

        return Promise.resolve(true);
      } catch (error) {
        context.dispatch(UPDATE_STATE_TYPE, {
          sendSmsTimeStamp: 0,
        });

        return Promise.resolve(false);
      }
    },

    // 提交绑卡申请，返回成功或者失败
    async submitCardBind(
      context: ActionContext<StateType, RootStateType>,
      payload: {
        entryFrom: FormInfomation;
        capitalCode: string;
        loanOrderId: string;
      },
    ): Promise<string | undefined> {
      try {
        const { smsCode, sendSmsOrBindCardResult } = context.state;

        if (sendSmsOrBindCardResult) {
          const { bankCardId } = await submitCardBind({
            smsCode,
            cardApplyId: sendSmsOrBindCardResult.cardApplyId,
            thirdApplyId: sendSmsOrBindCardResult.thirdApplyId,
            bindCardFlowType: BindCardFlowTypeMap[payload.entryFrom],
            capitalCode: payload.capitalCode,
            loanOrderId: payload.loanOrderId,
          });

          return Promise.resolve(bankCardId);
        }

        return Promise.resolve(undefined);
      } catch (error) {
        return Promise.resolve(undefined);
      }
    },
  },
};

store.registerModule('cardBind', generateMutations<StateType>(moduleStore));
